import axios from 'axios';
import authService from './auth.service';

const API_URL = process.env.REACT_APP_API_URL + '/auth';

// Crucial to Create a separate axios instance
const authenticatedAxiosInstance = axios.create({
  timeout: 30000, // 30 seconds - increased from 10s to support long-running operations
});
let refreshingFunc: Promise<unknown> | null = null;

const accessTokenRefresh = () => {
  return new Promise(async (resolve, reject) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      reject(new Error('localStorage not available'));
      return;
    }

    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userInfo = JSON.parse(user);
        const refreshToken = userInfo.refreshToken;

        const response = await axios.post(`${API_URL}/refresh-token`, {
          refreshToken,
        });

        if (response.data.accessToken) {
          const updatedUser = {
            ...userInfo,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          const newAccessToken = updatedUser.accessToken;
          resolve(newAccessToken);
        }
      } catch (error) {
        console.log('Error while parsing user info', error);
        reject(error);
      }
    } else {
      reject(new Error('No current user'));
    }
  });
};

authenticatedAxiosInstance.interceptors.request.use(
  function (config) {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userInfo = JSON.parse(user);
          const accessToken = userInfo.accessToken;
          if (accessToken) {
            config.headers['authorization'] = `Bearer ${accessToken}`;
          }
        } catch (error) {
          console.log('Error while parsing user info', error);
        }
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// @ts-ignore
function isUnauthorizedError(error) {
  return error?.response?.status === 401;
}

authenticatedAxiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  async responseError => {
    const originalConfig = responseError.config;

    // 🔴 MFA_REQUIRED must be handled BEFORE the refresh branch below.
    //
    // Since 2026-09-10 the API returns 403 when the caller holds a token from
    // a password-only signin (bead cjtr). This interceptor treats 403 as
    // "token is stale, refresh it" — but refreshing deliberately CARRIES THE
    // PENDING STATE THROUGH (otherwise refresh would itself be the MFA
    // bypass). So refreshing here would return another pending token, the
    // retry would 403 again, and the user would be bounced to /sign-in with no
    // explanation — an infinite-feeling login loop for exactly the users who
    // have MFA switched on.
    //
    // The right response is to send them to the verification page, which is
    // the one thing that CAN upgrade the token.
    if (responseError.response?.data?.code === 'MFA_REQUIRED') {
      if (typeof window !== 'undefined' && window.location.pathname !== '/verify') {
        window.location.href = '/verify';
      }
      return Promise.reject(responseError);
    }

    if (
      responseError.response?.data?.response === 'JWT validation failed' ||
      responseError.response?.status === 401 ||
      responseError.response?.status === 403
    ) {
      console.log(`${responseError.response.status} error, refreshing token.`);

      try {
        // @ts-ignore
        if (!refreshingFunc) {
          refreshingFunc = accessTokenRefresh();
        }

        const newAccessToken = await refreshingFunc;
        originalConfig.headers['authorization'] = `Bearer ${newAccessToken}`;

        // retry original request
        try {
          return await axios.request(originalConfig);
        } catch (retryRequestError) {
          // if original request with 401 then server determined invalid refresh token
          if (isUnauthorizedError(retryRequestError)) {
            throw retryRequestError;
          }
        }
      } catch {
        authService.logout();
        window.location.href = '/sign-in';
      } finally {
        refreshingFunc = null;
      }
    }
    return Promise.reject(responseError);
  }
);

export default authenticatedAxiosInstance;
